import { Fragment, useEffect, useRef, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { routeStores, type Group, type Member, type PendingInvite } from '@sp/shared'
import { useSignedIn } from '../session.tsx'
import { api } from '../lib/api.ts'
import { HttpError } from '../lib/http.ts'
import { useStores } from '../lib/groupData.ts'
import { useDesktop } from '../lib/useDesktop.ts'
import { formatDay, formatShortDay, validFor } from '../lib/format.ts'
import { errorMessage, useResource } from '../lib/useResource.ts'
import { Icon } from '../components/Icon.tsx'
import { Sheet } from '../components/Sheet.tsx'
import {
  Avatar,
  Button,
  Card,
  Divider,
  GroupButton,
  IconButton,
  Label,
  MenuItem,
  PageTitle,
  Paragraph,
  TextCol,
  TextField,
} from '../components/ui.tsx'
import { RouteLine } from '../components/bits.tsx'
import { DeskToolbar, Panel } from '../components/desk.tsx'
import { InviteSheet } from '../components/InviteSheet.tsx'
import { CreateGroupForm, GroupSwitcherSheet } from '../components/GroupSwitcherSheet.tsx'
import { AccountSheet } from '../components/AccountSheet.tsx'
import { useConfirm } from '../components/ConfirmSheet.tsx'
import { StoresEditor } from './StoresPage.tsx'

function offlineAware(err: unknown): string {
  return err instanceof HttpError && err.isOffline ? 'Skupinu jde měnit jen se signálem.' : errorMessage(err)
}

/** Clenove, cekajici pozvanky a obchody (Wise / Monarch v navrhu). */
export function GroupPage() {
  const s = useSignedIn()
  const g = s.group
  const navigate = useNavigate()
  const desktop = useDesktop()
  const stores = useStores(g.id)
  const invites = useResource<PendingInvite[]>(
    () => (g.personal ? Promise.resolve([]) : api.invites.list(g.id)),
    [g.id],
  )
  // Pozvanka, kterou mezitim nekdo prijal, nesmi dal viset mezi cekajicimi. Clenove
  // se obnovuji s prihlasenim (treba po navratu do appky), pozvanky se nactou s nimi;
  // po navratu do appky se nactou i samy, clenove se obnovuji s odstupem.
  const memberIds = g.members.map((m) => m.userId).join()
  const seenMembers = useRef(memberIds)
  const reloadInvites = invites.reload
  useEffect(() => {
    if (seenMembers.current === memberIds) return
    seenMembers.current = memberIds
    reloadInvites()
  }, [memberIds, reloadInvites])
  useEffect(() => {
    if (g.personal) return
    const onVisible = () => {
      if (document.visibilityState === 'visible') reloadInvites()
    }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onVisible)
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onVisible)
    }
  }, [g.personal, reloadInvites])

  const [inviteOpen, setInviteOpen] = useState(false)
  const [renameOpen, setRenameOpen] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [switcherOpen, setSwitcherOpen] = useState(false)
  const { confirm, confirmSheet } = useConfirm()
  const [accountOpen, setAccountOpen] = useState(false)
  const [member, setMember] = useState<Member | null>(null)
  const [error, setError] = useState<string | null>(null)

  const route = routeStores(stores.res.state === 'ok' ? stores.res.data : [])
  const pending = invites.res.state === 'ok' ? invites.res.data : []

  function memberSub(m: Member): string {
    const parts = [m.role === 'owner' ? 'Spravuje skupinu' : `Ve skupině od ${formatShortDay(m.joinedAt)}`]
    if (m.userId === s.user.id) parts.push('ty')
    return parts.join(' · ')
  }

  async function revoke(invite: PendingInvite) {
    setError(null)
    try {
      await api.invites.revoke(g.id, invite.id)
      invites.setData((prev) => prev.filter((i) => i.id !== invite.id))
    } catch (err) {
      setError(offlineAware(err))
    }
  }

  async function leave() {
    const last = g.members.length === 1
    const question = last
      ? `Jsi ve skupině „${g.name}“ poslední. Když ji opustíš, smaže se i se šablonami, obchody a nákupy.`
      : `Zpátky do skupiny „${g.name}“ se dostaneš jen s novou pozvánkou.`
    if (!(await confirm({ title: last ? 'Opustit a smazat skupinu?' : 'Opustit skupinu?', text: question, confirmLabel: 'Opustit skupinu', danger: true }))) return
    setError(null)
    try {
      await api.groups.removeMember(g.id, s.user.id)
      await s.refresh()
      navigate('/')
    } catch (err) {
      setError(offlineAware(err))
    }
  }

  async function removeMember(m: Member) {
    if (!(await confirm({ title: `Odebrat ${m.name}?`, text: `Ze skupiny „${g.name}“. Zpátky se dostane jen s novou pozvánkou.`, confirmLabel: 'Odebrat ze skupiny', danger: true }))) return
    setError(null)
    try {
      await api.groups.removeMember(g.id, m.userId)
      await s.refresh()
      setMember(null)
    } catch (err) {
      setError(offlineAware(err))
    }
  }

  const subtitle = g.personal ? 'Osobní skupina' : `Založena ${formatDay(g.createdAt)}`
  const renameButton = (
    <IconButton icon="pencil" label="Přejmenovat skupinu" bordered={desktop} onClick={() => setRenameOpen(true)} />
  )

  const personalCard = g.personal && (
    <Card gap="sm">
      <span className="card-title">Jen pro tebe</span>
      <Paragraph>
        Do osobní skupiny nikoho nepozveš. Na společné nákupy založ skupinu a pozvi do ní ostatní – každá má vlastní
        šablonu, obchody a nákupy.
      </Paragraph>
      <Button kind="primary" icon="plus" className="self-start" onClick={() => setCreateOpen(true)}>
        Založit skupinu
      </Button>
    </Card>
  )

  const memberRows = g.members.map((m, index) => (
    <Fragment key={m.userId}>
      {index > 0 && <Divider />}
      <div className="member-row">
        <Avatar name={m.name} size={40} />
        <TextCol title={m.name} sub={memberSub(m)} />
        {g.role === 'owner' && m.userId !== s.user.id && (
          <IconButton icon="more" tone="muted" label={`Možnosti člena ${m.name}`} onClick={() => setMember(m)} />
        )}
      </div>
    </Fragment>
  ))

  const pendingRows = pending.map((invite, index) => (
    <Fragment key={invite.id}>
      {index > 0 && <Divider />}
      <div className="member-row">
        <span className="round-icon is-bordered">
          <Icon name="link" size={20} />
        </span>
        <TextCol title={invite.label ?? 'Pozvánka bez jména'} sub={validFor(invite.expiresAt)} />
        <Button kind="danger" compact onClick={() => void revoke(invite)}>
          Zrušit
        </Button>
      </div>
    </Fragment>
  ))

  const leaveButton = !g.personal && (
    <Button kind="danger" compact className="self-start" onClick={() => void leave()}>
      Opustit skupinu
    </Button>
  )

  const sheets = (
    <>
      <InviteSheet
        open={inviteOpen}
        group={g}
        onClose={() => {
          setInviteOpen(false)
          invites.reload()
        }}
      />
      <RenameSheet open={renameOpen} onClose={() => setRenameOpen(false)} group={g} />
      <Sheet open={createOpen} onClose={() => setCreateOpen(false)} title="Založit skupinu">
        <CreateGroupForm
          onCancel={() => setCreateOpen(false)}
          onCreated={(groupId) => {
            s.selectGroup(groupId)
            setCreateOpen(false)
          }}
        />
      </Sheet>
      <GroupSwitcherSheet
        open={switcherOpen}
        onClose={() => setSwitcherOpen(false)}
        onOpenAccount={() => {
          setSwitcherOpen(false)
          setAccountOpen(true)
        }}
      />
      <AccountSheet open={accountOpen} onClose={() => setAccountOpen(false)} />
      {confirmSheet}
      <Sheet open={member !== null} onClose={() => setMember(null)} title={member?.name ?? ''} subtitle={g.name}>
        {member && (
          <div className="menu">
            <MenuItem icon="trash" tone="error" label="Odebrat ze skupiny" onClick={() => void removeMember(member)} />
          </div>
        )}
      </Sheet>
    </>
  )

  if (desktop) {
    return (
      <div className="desk-view-main desk-narrow">
        <DeskToolbar
          title={g.name}
          sub={subtitle}
          actions={
            <>
              {renameButton}
              {!g.personal && (
                <Button kind="primary" icon="user_plus" onClick={() => setInviteOpen(true)}>
                  Pozvat do skupiny
                </Button>
              )}
            </>
          }
        />
        <div className="desk-grid-2">
          <div className="stack">
            {personalCard}
            <Panel title="Členové" meta={String(g.members.length)} className="card-gap-none">
              {memberRows}
            </Panel>
            {!g.personal && pending.length > 0 && (
              <Panel title="Čeká na přijetí" meta={String(pending.length)}>
                {pendingRows}
              </Panel>
            )}
            {error && <Paragraph tone="error">{error}</Paragraph>}
            {leaveButton}
          </div>
          <Panel title="Obchody na trase">
            <StoresEditor compact />
          </Panel>
        </div>
        {sheets}
      </div>
    )
  }

  return (
    <>
      <div className="group-head">
        <GroupButton name={g.name} onClick={() => setSwitcherOpen(true)} className="group-head-switch" />
        {renameButton}
      </div>
      <Paragraph size="meta">{s.groups.length > 1 ? `${subtitle} · ťukni na název pro jinou skupinu` : subtitle}</Paragraph>

      <Card padding="none" gap="none">
        <button type="button" className="member-row link-row" onClick={() => setAccountOpen(true)}>
          <Avatar name={s.user.name} size={36} />
          <TextCol title={`Ty · ${s.user.name}`} sub="Jméno, heslo, upozornění, vzhled a písmo" />
          <Icon name="chevron_right" size={20} className="tone-muted" />
        </button>
      </Card>

      {personalCard}

      <Label>Členové</Label>
      <Card padding="none" gap="none">
        {memberRows}
      </Card>

      {!g.personal && pending.length > 0 && (
        <>
          <Label>Čeká na přijetí</Label>
          <Card padding="none" gap="none">
            {pendingRows}
          </Card>
        </>
      )}

      {!g.personal && (
        <Button kind="primary" icon="user_plus" full onClick={() => setInviteOpen(true)}>
          Pozvat do skupiny
        </Button>
      )}

      <Label>Obchody</Label>
      <Card padding="none" gap="none">
        <button type="button" className="member-row link-row" onClick={() => navigate('/skupina/obchody')}>
          <span className="round-icon">
            <Icon name="store" size={20} />
          </span>
          <TextCol
            title="Obchody na trase"
            sub={route.length > 0 ? <RouteLine stores={route} /> : 'Zatím žádné – přidej první'}
          />
          <Icon name="chevron_right" size={20} className="tone-muted" />
        </button>
      </Card>

      {error && <Paragraph tone="error">{error}</Paragraph>}
      {leaveButton}
      {sheets}
    </>
  )
}

function RenameSheet({ open, onClose, group }: { open: boolean; onClose(): void; group: Group }) {
  const s = useSignedIn()
  const [name, setName] = useState(group.name)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setName(group.name)
    setError(null)
  }, [open, group.name])

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (!name.trim()) {
      setError('Název nesmí být prázdný')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await api.groups.rename(group.id, name.trim())
      await s.refresh()
      onClose()
    } catch (err) {
      setError(offlineAware(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Sheet open={open} onClose={onClose} title="Přejmenovat skupinu">
      <form className="stack" onSubmit={(e) => void submit(e)}>
        <TextField label="Název skupiny" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} autoFocus />
        {error && <Paragraph tone="error">{error}</Paragraph>}
        <Button kind="primary" type="submit" full disabled={busy}>
          Uložit
        </Button>
      </form>
    </Sheet>
  )
}
